import Chart from 'react-apexcharts'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import { useCallback, useEffect, useState } from 'react'
import { twinFetchPostJSON } from '../../../../utils/globals/data'
import { getISOCurrencyMain } from '../../../../utils/reducers/getters'
import { preciseDecimalSum } from '@teinor/erp/prices'
import { getCurrencySymbol } from '../../../../utils/globals/price'
import { QuoteMultipleChartData, QuoteMultipleChartDataKeys } from './types'
import './quotemultiplechartdata.sass'
import useDictMonths from '../../../../utils/hooks/useDictMonths'
import { quoteOrderStatus, QuoteOrderStatusInversed, QuoteOrderStatusKeys } from '@teinor/erp/types/company/order/quote/quoteOrder'
import { TwinDictionary } from '../../../../utils/globals/dictionary'
import { displayFormatedNumber } from '../../../../utils/globals/numbers'

interface DSBQuoteMultipleChartDataProps extends DSBQuoteMultipleChartDataLogic {
    title?: string
}

const DSBQuoteMultipleChartData = ({title, end_date, start_date }: DSBQuoteMultipleChartDataProps) => {
    const { t } = useTwinTranslation()
    const { quotes } = useDSBQuoteMultipleChartDataLogic({end_date, start_date})
    const mainCurrency = getISOCurrencyMain()
    const currencySymbol = getCurrencySymbol(mainCurrency)
    const { arrMonthShort } = useDictMonths()
    const customlegendFormatter = (name: any, opts: any) => {
        return `
            <div class='legend-label'>${name}</div>
            <div class='legend-value'>${displayFormatedNumber(opts.w.globals.seriesTotals[opts.seriesIndex].toFixed(2))}${currencySymbol}</div>
        `
    }
    const series = [
        {
            name: t('pending', 'Pendientes'),
            type: 'column',
            data: quotes.waitingResponse
        },
        {
            name: t('sent', 'Enviados'),
            type: 'column',
            data: quotes.sent
        },
        {
            name: t('accepted', 'Aceptados'),
            type: 'column',
            data: quotes.accepted
        },
        {
            name: t('cancelleds', 'Cancelados'),
            type: 'column',
            data: quotes.denied
        },
        {
            name: t('total', 'Total'),
            type: 'area',
            data: quotes.totals
        }
    ]
    return (
        <div className='flex flex-col gap-10 flex-auto h-1 quote_multiplechart_data'>
            <h3>{title}</h3>
            <div className='flex-auto h-1 flex flex-col'>
                <div className='flex-auto'>
                    <Chart options={{
                        chart: {
                            type: 'line',
                            stacked: false,
                            events: {
                                mounted: (chart) => {
                                    chart.windowResizeHandler()
                                }
                            },

                            toolbar: {
                                show: false
                            },
                            zoom: {
                                enabled: true
                            }
                        },
                        plotOptions: {
                            bar: {
                                horizontal: false,
                                columnWidth: '60%',
                            },
                        },
                        stroke: {
                            show: true,
                            curve: 'smooth',
                            width: 2,
                        },
                        yaxis: {
                            labels: {
                                style: {
                                    colors: '#8F9BA4',
                                    fontFamily: 'Roboto'
                                }
                            },
                        },
                        xaxis: {
                            labels: {
                                style: {
                                    fontFamily: 'Roboto',
                                    colors: '#8F9BA4',
                                }
                            },
                            categories: arrMonthShort
                        },
                        fill: {
                            opacity: 1,
                        },
                        legend: {
                            position: 'top',
                            horizontalAlign: 'left',
                            formatter: customlegendFormatter,
                            fontFamily: 'Roboto',
                            markers: {
                                offsetY: 3
                            }
                        },
                        colors: ['#FFA200', '#865fe5', '#1DB27B', '#EB2D53', '#DBF1FF'],
                        tooltip: {
                            y: {
                                formatter: function (val) {
                                    return displayFormatedNumber(val.toFixed(2)) + ' ' + currencySymbol
                                }
                            }
                        }
                    }} series={series} type="line" height={'100%'} />
                </div>
            </div>
        </div>
    )
}

interface DSBQuoteMultipleChartDataLogic {
    start_date: Date
    end_date: Date
}

const useDSBQuoteMultipleChartDataLogic = ({end_date, start_date}: DSBQuoteMultipleChartDataLogic) => {
    const [quotes, setQuotes] = useState<QuoteMultipleChartData>({ accepted: [], acceptedTotal: 0, denied: [], deniedTotal: 0, totals: [], totalTotal: 0, waitingResponse: [], waitingResponseTotal: 0, sent: [], sentTotal: 0 })

    const getData = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/order/quote/getAllQuoteOrdersForStats', {
            attributes: ['total_without_vat', 'status', 'createdAt'],
            where: { 'QuoteOrder.createdAt': { '$betweenN': [start_date, end_date] }, is_last: true }
        })
        const quoteStatus: TwinDictionary = { accepted: Array(12).fill(0), acceptedTotal: 0, denied: Array(12).fill(0), deniedTotal: 0, totals: Array(12).fill(0), totalTotal: 0, waitingResponse: Array(12).fill(0), waitingResponseTotal: 0, sent: Array(12).fill(0), sentTotal: 0 }


        if (result) {
            for (const element of result) {
                const date = new Date(element.createdAt)
                const month = date.getMonth()
                const myStatusInversed = element.status !== QuoteOrderStatusInversed['processed'] ? quoteOrderStatus[element.status as QuoteOrderStatusKeys] as QuoteMultipleChartDataKeys : 'accepted'
                if (Array.isArray(quoteStatus[myStatusInversed])) {
                    const myTotal = parseFloat(element.total_without_vat)
                    quoteStatus[myStatusInversed][month] = parseFloat(preciseDecimalSum(myTotal, quoteStatus[myStatusInversed][month]).toFixed(2))
                    quoteStatus['totals'][month] = parseFloat(preciseDecimalSum(myTotal, quoteStatus['totals'][month]).toFixed(2))
                    quoteStatus[myStatusInversed + 'Total'] = parseFloat(preciseDecimalSum(myTotal, quoteStatus[myStatusInversed + 'Total']).toFixed(2))
                    quoteStatus['totalTotal'] = parseFloat(preciseDecimalSum(myTotal, quoteStatus['totalTotal']).toFixed(2))
                }
            } 
            setQuotes(quoteStatus as QuoteMultipleChartData )
        }
    }, [setQuotes, end_date, start_date])


    useEffect(() => {
        getData()
    }, [getData])
    return { quotes }
}


export default DSBQuoteMultipleChartData